// Uncaught errors to Satori Core (shared/API.md §45): `window` `error` + `unhandledrejection`, sent at once
// through the chat domain's `/core-api/chat/diagnostics/exit` (nginx turns the session cookie into the bearer).
// At most 5 per page load, each distinct message+stack once; nothing is ever shown to the person.

export const ERROR_REPORT_PATH = '/core-api/chat/diagnostics/exit';
export const MAX_PER_PAGE = 5;
/** `keepalive` bodies are capped at 64 KB by the browser; the head of a stack is what names the cause. */
export const TRACE_LIMIT = 32 * 1024;

/** Browser noise that is not our bug: cross-origin scripts (no detail at all) and ResizeObserver's benign loop note. */
export function isNoise(message: string): boolean {
  return message === 'Script error.' || message === '' || /ResizeObserver loop/i.test(message);
}

export interface ErrorReportInput {
  message: string;
  stack?: string | null;
  where: 'error' | 'unhandledrejection';
  at: number;
  version: string;
  build: number;
  userId: string | null;
  userAgent: string;
}

export function buildReport(input: ErrorReportInput): Record<string, unknown> {
  const stack = (input.stack ?? '').slice(0, TRACE_LIMIT);
  return {
    reason: 'error',
    at: input.at,
    platform: 'web',
    description: `${input.message} [${input.where}]`.slice(0, 2000),
    ...(stack ? { trace: stack } : {}),
    version_name: input.version,
    version_code: input.build,
    device: browserName(input.userAgent),
    os: osName(input.userAgent),
    ...(input.userId ? { mm_user_id: input.userId } : {}),
  };
}

/** Decides which errors of this page load go out: not noise, not seen before, not over the cap. */
export class ErrorReportGate {
  private readonly seen = new Set<string>();

  allow(message: string, stack?: string | null): boolean {
    if (isNoise(message) || this.seen.size >= MAX_PER_PAGE) return false;
    const key = `${message}\n${stack ?? ''}`;
    if (this.seen.has(key)) return false;
    this.seen.add(key);
    return true;
  }
}

export function browserName(ua: string): string {
  // Edge and Opera also say «Chrome/…», so they are looked for first.
  const m = /(Edg|OPR)\/(\d+)/.exec(ua) ?? /(Chrome|Firefox|Version)\/(\d+)/.exec(ua);
  if (!m) return 'Navegador';
  const name = m[1] === 'Edg' ? 'Edge' : m[1] === 'OPR' ? 'Opera' : m[1] === 'Version' ? 'Safari' : m[1];
  return `${name} ${m[2]}`;
}

export function osName(ua: string): string {
  if (/iPhone|iPad/.test(ua)) return 'iOS';
  if (/Android/.test(ua)) return 'Android';
  if (/Mac OS X/.test(ua)) return 'macOS';
  if (/Windows/.test(ua)) return 'Windows';
  if (/Linux/.test(ua)) return 'Linux';
  return 'desconocido';
}
